<template>
  <BaseModal
    :heading="$t('reports.totals.pos_tips')"
    @close="$emit('close')"
    testId="pos-payments-tips"
  >
    <div :class="$style.header">
      <DateSelect
        v-bind="{ startDate, endDate }"
        :label="$t('global.actions.period')"
        @select="onDateSelect"
      />
      <div>
        <BaseSpinner
          v-if="totalsLoading"
          inline
        />
        <Flex v-else>
          <div>
            <BaseHeading size="s">{{ $t('global.total_count') }}</BaseHeading>
            <BaseHeading
              size="xl"
              v-test="'pos-payments-tips-count'"
            >
              {{ totals.count }}
            </BaseHeading>
          </div>
          <div>
            <BaseHeading size="s">{{ $t('global.total_amount') }}</BaseHeading>
            <BaseHeading
              size="xl"
              v-test="'pos-payments-tips-total'"
            >
              {{ filters.currency(totals.totalTipAmount) }}
            </BaseHeading>
          </div>
        </Flex>
      </div>
    </div>
    <Flex
      v-if="firstFetchFinished && posPayments.length === 0 && !loading"
      justify="center"
      padding
    >
      {{ $t('global.no_results') }}
    </Flex>
    <BaseTable
      v-else-if="posPayments.length"
      :headers="[
        $t('global.employee'),
        $t('global.date'),
        $t('reports.totals.tip_amount')
      ]"
      :rows="
        posPayments.map((posPayment) => ({
          id: posPayment.id,
          cells: [
            posPayment.employees.map((employee) => employee.name).join(', '),
            filters.dateTime(posPayment.createdAt),
            filters.currency(posPayment.tipAmount)
          ],
          actions: [orderPdfIcon()]
        }))
      "
      @action="showPdf"
      v-test="'pos-payments-table'"
    />
    <LoadMore
      v-show="firstFetchFinished && !allDataFetched"
      @shown="fetchMore"
    />
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { usePagination } from '@/apollo/pagination';
import LoadMore from '@/components/LoadMore.vue';
import filters from '@/filters';
import { orderPdfIcon, orderPdfRoute } from '@/helpers/order-pdf-route';
import DateSelect from '../components/DateSelect.vue';

const props = defineProps<{
  startDate: string;
  endDate: string;
}>();

defineEmits(['close']);

const {
  posPayments,
  firstFetchFinished,
  allDataFetched,
  variables,
  fetchMore,
  loading,
} = usePagination({
  fieldName: 'posPayments',
  query: gql`
    query getPosPayments(
      $startDate: DateTime
      $endDate: DateTime
      $onlyTips: Boolean
      $pagination: PaginationAttributes!
    ) {
      posPayments(
        startDate: $startDate
        endDate: $endDate
        onlyTips: $onlyTips
        pagination: $pagination
      ) {
        id
        orderId
        amount
        tipAmount
        createdAt
        employees {
          id
          name
        }
      }
    }
  `,
  variables: {
    startDate: props.startDate,
    endDate: props.endDate,
    onlyTips: true,
  },
});

const showPdf = ({ id }: { id: string }) => {
  const posPayment = posPayments.value.find(
    (posPayment) => posPayment.id === id,
  );

  orderPdfRoute(posPayment.orderId);
};

const onDateSelect = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) => {
  variables.startDate = startDate;
  variables.endDate = endDate;
};

const { result, loading: totalsLoading } = useQuery(
  gql`
  query getPosTipTotals($startDate: DateTime, $endDate: DateTime) {
    posTipTotals(startDate: $startDate, endDate: $endDate) {
      count
      totalTipAmount
    }
  }
`,
  () => ({
    startDate: variables.startDate,
    endDate: variables.endDate,
  }),
);

const totals = computed(
  () => result.value?.posTipTotals || { count: 0, totalTipAmount: 0 },
);
</script>

<style
  lang="scss"
  module
>
.header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: $spacing;
  margin-bottom: $spacing;
}
</style>
